// Reusable component that turns [KEY] placeholders into <a> links. Can be used to create one or more html links to a localized text.

import React, { useMemo } from 'react';

export interface TranslatableLinkSpanProps {
    // Localized text containing zero or more [KEY] placeholders.
    // Key is the text that will be displayed as a link so it should be localized.
    text: string;
    // Array of URLs OR click handlers. First [KEY] maps to urls[0], etc.
    urls: Array<
        | string
        | ((e: React.MouseEvent<HTMLAnchorElement>, linkLabel: string, index: number) => void)
    >;
    // Optional props for the wrapper <span>
    spanProps?: React.HTMLAttributes<HTMLSpanElement> & { [key: string]: any };
    // Optional props for the generated <a> elements: includes anchor-specific attributes like target, rel, href, etc.
    anchorProps?: React.AnchorHTMLAttributes<HTMLAnchorElement>;
}

export const TranslatableLinkSpan: React.FC<TranslatableLinkSpanProps> = ({
    text,
    urls,
    spanProps,
    anchorProps,
}) => {
    // Split the text by placeholder(s) and create an array of React nodes.
    // Memoized for performance to avoid parsing on every render.
    const memoizedParts = useMemo<React.ReactNode[]>(() => {
        const parts: React.ReactNode[] = [];
        const regex = /\[([^\]]+)\]/g;
        let lastIndex = 0;
        let linkIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(text)) !== null) {
            const [placeholder, linkLabel] = match;

            // Text before this placeholder
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }

            // Determine URL for this link, if available
            const url = linkIndex < urls.length ? urls[linkIndex] : undefined;

            if (typeof url === 'string') {
                parts.push(
                    <a key={match.index} {...anchorProps} href={url}>
                        {linkLabel}
                    </a>
                );
            } else if (typeof url === 'function') {
                parts.push(
                    <a
                        key={match.index}
                        {...anchorProps}
                        href={anchorProps?.href ?? '#'}
                        onClick={(e) => {
                            e.preventDefault();
                            anchorProps?.onClick?.(e);
                            url(e, linkLabel, linkIndex);
                        }}
                    >
                        {linkLabel}
                    </a>
                );
            } else {
                parts.push(linkLabel);
            }
            linkIndex++;
            lastIndex = match.index + placeholder.length;
        }

        // Any trailing text after the last placeholder
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }
        return parts;
    }, [text, urls, anchorProps]);

    return <span {...spanProps}>{memoizedParts}</span>;
};
